import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { AdviseSupplier, SupplierDirectoryRow } from '../../../types/advise';
import { PRICING_MODEL_OPTIONS, PRODUCT_CATEGORY_OPTIONS, productCategoryLabel } from '../../../constants/advise';
import { fetchAdviseSupplierById } from '../../../Api/Advise/Suppliers';
import { TextSpan } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { isValidEmail } from '../../../utils/email';

interface SupplierFormDialogProps {
  open: boolean;
  onClose: () => void;
  // Resolves to why the save was refused, or null once saved; a supplier with an id is an edit of that record.
  onSave: (supplier: Partial<AdviseSupplier>) => Promise<string | null>;
  supplier?: AdviseSupplier | null;
  // The directory, whose Salesforce suppliers can be linked.
  directory: SupplierDirectoryRow[];
}

const EMPTY_FORM: Partial<AdviseSupplier> = {
  supplier_name: '',
  headquarters: '',
  pricing_model: '',
  product_category: '',
  strengths: [],
  limitations: [],
  channel_manager_name: '',
  channel_manager_email: '',
  channel_manager_phone: '',
  sf_supplier_id: undefined,
  sf_supplier_name: undefined
};

// upstack_api's Advise::Supplier::TEXT_LIMIT, for each text field and each Strengths or Limitations entry.
const SUPPLIER_TEXT_LIMIT = 200;
const LIMITED = { maxLength: SUPPLIER_TEXT_LIMIT };

// As upstack_api saves them, so the chips show what the save will keep.
const chipsFrom = (values: string[]) => [...new Set(values.map((value) => value.trim()).filter(Boolean))];

const LIST_FIELDS = [
  { field: 'strengths', label: 'Strengths', color: 'success' },
  { field: 'limitations', label: 'Limitations', color: 'warning' }
] as const;

const LINK_FILLS = [
  ['supplier_name', (row: SupplierDirectoryRow | null) => row?.supplier_name ?? ''],
  ['headquarters', (row: SupplierDirectoryRow | null) => row?.location ?? '']
] as const;

const OPEN_FAILED = "Couldn't open that supplier's record. Please try again.";

const CHANNEL_MANAGER_FIELDS = [
  { field: 'channel_manager_name', label: 'Name', placeholder: 'Full name' },
  { field: 'channel_manager_email', label: 'Email', placeholder: 'name@company.com' },
  { field: 'channel_manager_phone', label: 'Phone', placeholder: '(555) 555-5555' }
] as const;

/** Adds an Advise-only supplier, optionally linked to a Salesforce one, or edits an existing Advise supplier. */
export const SupplierFormDialog = ({ open, onClose, onSave, supplier, directory }: SupplierFormDialogProps) => {
  // A Salesforce supplier already in Advise, picked from Link while adding, which opens its record instead.
  const [existing, setExisting] = useState<AdviseSupplier | null>(null);
  const linkFilled = useRef<Partial<Record<(typeof LINK_FILLS)[number][0], string>>>({});
  const isEdit = !!(supplier ?? existing);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<AdviseSupplier>>(EMPTY_FORM);

  useEffect(() => {
    setForm(supplier ? { ...supplier } : EMPTY_FORM);
    setExisting(null);
    linkFilled.current = {};
    setRefusal(null);
  }, [supplier, open]);

  // An edit can link only a Salesforce supplier no other Advise supplier links, which the API would refuse.
  const linkable = useMemo(
    () => directory.filter((row) => row.sf_supplier_id && (!supplier || !row.id || row.id === supplier.id)),
    [directory, supplier]
  );
  // A Salesforce row's categories are its Products, which an Advise supplier's Product Category never takes.
  const categories = useMemo(() => {
    const listed = PRODUCT_CATEGORY_OPTIONS.map((option) => option.value);
    const current = form.product_category;
    return current && !listed.includes(current) ? [...listed, current] : listed;
  }, [form.product_category]);

  const updateField = (field: keyof AdviseSupplier, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));
  const badEmail = !!form.channel_manager_email?.trim() && !isValidEmail(form.channel_manager_email);

  const openExisting = async (id: string) => {
    setSaving(true);
    const { data } = await fetchAdviseSupplierById(id);
    setSaving(false);
    if (!data) return setRefusal(OPEN_FAILED);
    setRefusal(null);
    setExisting(data);
    linkFilled.current = {};
    setForm({ ...data });
  };

  // A link fills what is empty and replaces what the last link filled, never what the user typed; null unlinks.
  const link = (row: SupplierDirectoryRow | null) => {
    if (!supplier && row?.id) return openExisting(row.id);
    const base = existing ? EMPTY_FORM : form;
    if (existing) setExisting(null);
    const next: Partial<AdviseSupplier> = {
      ...base,
      sf_supplier_id: row?.sf_supplier_id ?? null,
      sf_supplier_name: row?.supplier_name ?? null
    };
    const filled: typeof linkFilled.current = {};
    LINK_FILLS.forEach(([field, valueFrom]) => {
      const current = base[field];
      if (current && current !== linkFilled.current[field]) return;
      next[field] = valueFrom(row);
      filled[field] = valueFrom(row);
    });
    linkFilled.current = filled;
    setForm(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const refused = await onSave(form);
      if (refused) setRefusal(refused);
      else onClose();
    } finally {
      setSaving(false);
    }
  };

  const linked = form.sf_supplier_id ? linkable.find((row) => row.sf_supplier_id === form.sf_supplier_id) : undefined;
  const linkValue =
    linked ??
    (form.sf_supplier_id
      ? {
          id: null,
          sf_supplier_id: form.sf_supplier_id,
          supplier_name: form.sf_supplier_name || form.sf_supplier_id,
          location: null,
          pricing_model: null,
          categories: []
        }
      : null);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle className="flex items-center justify-between">
        {isEdit ? 'Edit Supplier' : 'Add Supplier'}
        <IconButton
          aria-label="Close"
          size="small"
          disabled={saving}
          onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4">
        {!isEdit && (
          <Alert
            severity="info"
            icon={false}>
            This supplier will be added to Advise only. It won&apos;t be created in Salesforce.
          </Alert>
        )}
        {existing && (
          <Alert severity="info">
            {existing.supplier_name} is already in Advise, so its record is open for editing.
          </Alert>
        )}
        {refusal && <Alert severity="error">{refusal}</Alert>}
        <Autocomplete
          options={linkable}
          value={linkValue}
          disabled={saving}
          getOptionLabel={(row) => row.supplier_name}
          renderOption={(props, row) => (
            <li
              {...props}
              key={row.sf_supplier_id}>
              {row.supplier_name}
              {row.id && !supplier && (
                <TextSpan
                  size="xs"
                  color="grey6"
                  className="ml-2">
                  In Advise
                </TextSpan>
              )}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.sf_supplier_id === value.sf_supplier_id}
          onChange={(_, row) => link(row)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Link to Existing Supplier (Optional)"
              placeholder="Search existing suppliers…"
              helperText="Start typing to link a Salesforce supplier record."
              size="small"
            />
          )}
        />
        <TextField
          label="Supplier Name"
          placeholder="e.g. Acme Communications"
          required
          size="small"
          disabled={saving}
          inputProps={LIMITED}
          value={form.supplier_name || ''}
          onChange={(e) => updateField('supplier_name', e.target.value)}
        />
        <TextField
          label="Headquarters"
          placeholder="City, State"
          size="small"
          disabled={saving}
          inputProps={LIMITED}
          value={form.headquarters || ''}
          onChange={(e) => updateField('headquarters', e.target.value)}
        />
        <Box className="grid grid-cols-2 gap-4">
          <TextField
            select
            label="Product Category"
            size="small"
            disabled={saving}
            value={form.product_category || ''}
            onChange={(e) => updateField('product_category', e.target.value)}>
            {categories.map((category) => (
              <MenuItem
                key={category}
                value={category}>
                {productCategoryLabel(category)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Pricing Model"
            size="small"
            disabled={saving}
            value={form.pricing_model || ''}
            onChange={(e) => updateField('pricing_model', e.target.value)}>
            {PRICING_MODEL_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {LIST_FIELDS.map(({ field, label, color }) => (
          <Autocomplete
            key={field}
            multiple
            freeSolo
            options={[]}
            disabled={saving}
            value={form[field] || []}
            onChange={(_, values) => updateField(field, chipsFrom(values))}
            renderTags={(values, getTagProps) =>
              values.map((value, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={value}
                  label={value}
                  size="small"
                  color={color}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder="Press Enter to add…"
                size="small"
                inputProps={{ ...params.inputProps, ...LIMITED }}
              />
            )}
          />
        ))}
        <Box className="flex flex-col gap-2 rounded border border-grey-2 bg-grey-1 p-3">
          <Box className="text-sm font-semibold">Channel Manager</Box>
          <Box className="grid grid-cols-3 gap-2">
            {CHANNEL_MANAGER_FIELDS.map(({ field, label, placeholder }) => (
              <TextField
                key={field}
                label={label}
                placeholder={placeholder}
                size="small"
                disabled={saving}
                inputProps={LIMITED}
                error={field === 'channel_manager_email' && badEmail}
                helperText={field === 'channel_manager_email' && badEmail ? 'Enter a valid email' : undefined}
                value={form[field] || ''}
                onChange={(e) => updateField(field, e.target.value)}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onClose}
          disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={handleSave}
          disabled={!form.supplier_name?.trim() || badEmail || saving}>
          {saving ? <ComponentSpinner sx={{ height: '19px' }} /> : isEdit ? 'Save Changes' : 'Create Supplier'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
